import { Repository, RepositoryBase } from '@/repository/base';
import PutSingleAnswer from '@/models/entry/PutSingleAnswer';
import PutAllQuestionAnswer from '@/models/entry/PutAllQuestionAnswer';

/**
 * 検定申込関連リポジトリアクセス
 */
export default class EntryRepository extends RepositoryBase {
  private repository = '/entries';

  /**
   * 検定申込
   */
  entry(rankCode: string, serialCode: string) {
    const config = {
      headers: {
        'x-exam-key': process.env.VUE_APP_EXAM_SECRET_KEY,
      },
    };
    // eslint-disable-next-line @typescript-eslint/camelcase
    return Repository.post(`${this.repository}`, { exam_rank_code: rankCode, serial_code: serialCode }, config);
  }

  /**
   * 受験申込検索
   */
  getEntry() {
    const config = {
      headers: {
        'x-exam-key': process.env.VUE_APP_EXAM_SECRET_KEY,
      },
    };
    // eslint-disable-next-line @typescript-eslint/camelcase
    return Repository.get(`${this.repository}`, config);
  }

  /**
   * 獲得特典の取得
   * @param entryCode base64エンコードされた受験申込コード
   * @param resultCode base64エンコードされた受験結果コード
   */
  getBenefits(entryCode: string, resultCode: string) {
    const config = {
      headers: {
        'x-exam-key': process.env.VUE_APP_EXAM_SECRET_KEY,
      },
    };
    return Repository.get(`${this.repository}/${entryCode}/results/${resultCode}/benefits`, config);
  }

  /**
   * 受験申込コード指定での取得
   * @param entryCode base64エンコードされた受験申込コード
   */
  getEntryWithEntryCode(entryCode: string) {
    const config = {
      headers: {
        'x-exam-key': process.env.VUE_APP_EXAM_SECRET_KEY,
      },
    };
    return Repository.get(`${this.repository}/${entryCode}`, config);
  }

  /**
   * 検定問題検索
   * @param entryCode base64エンコードされた受験申込コード
   */
  getQuestion(entryCode: string) {
    const config = {
      headers: {
        'x-exam-key': process.env.VUE_APP_EXAM_SECRET_KEY,
      },
    };
    return Repository.get(`${this.repository}/${entryCode}/questions`, config);
  }

  /**
   * 受験結果の取得
   * @param entryCode base64エンコードされた受験申込コード
   * @param resultCode base64エンコードされた受験結果コード
   */
  getResult(entryCode: string, resultCode: string) {
    const config = {
      headers: {
        'x-exam-key': process.env.VUE_APP_EXAM_SECRET_KEY,
      },
    };
    return Repository.get(`${this.repository}/${entryCode}/results/${resultCode}`, config);
  }

  /**
   * 模範解答一覧取得
   * @param entryCode base64エンコードされた受験申込コード
   * @param resultCode base64エンコードされた受験結果コード
   */
  getModelAnswers(entryCode: string, resultCode: string) {
    const config = {
      headers: {
        'x-exam-key': process.env.VUE_APP_EXAM_SECRET_KEY,
      },
    };
    return Repository.get(`${this.repository}/${entryCode}/results/${resultCode}/model_answers`, config);
  }

  /**
   * 解答検索
   * @param entryCode base64エンコードされた受験申込コード
   * @param resultCode base64エンコードされた受験結果コード
   */
  getAnswer(entryCode: string, resultCode: string) {
    const config = {
      headers: {
        'x-exam-key': process.env.VUE_APP_EXAM_SECRET_KEY,
      },
    };
    return Repository.get(`${this.repository}/${entryCode}/results/${resultCode}/details`, config);
  }

  /**
   * 受験開始
   * @param entryCode base64エンコードされた受験申込コード
   */
  postExamStart(entryCode: string, resultCode: string) {
    const config = {
      headers: {
        'x-exam-key': process.env.VUE_APP_EXAM_SECRET_KEY,
      },
    };
    // eslint-disable-next-line @typescript-eslint/camelcase
    return Repository.post(`${this.repository}/${entryCode}/start`, { examination_result_code: resultCode }, config);
  }

  /**
   * 問題解答
   * @param entryCode base64エンコードされた受験申込コード
   * @param resultCode base64エンコードされた受験結果コード
   * @param questionCode base64エンコードされた問題コード
   * @param answers 解答内容オブジェクト
   */
  putAnswer(entryCode: string, resultCode: string, questionCode: string, answer: PutSingleAnswer) {
    const config = {
      headers: {
        'x-exam-key': process.env.VUE_APP_EXAM_SECRET_KEY,
      },
    };
    return Repository.put(`${this.repository}/${entryCode}/results/${resultCode}/questions/${questionCode}`,
      this.convert.serialize(answer), config);
  }

  /**
   * 問題解答終了
   * @param entryCode base64エンコードされた受験申込コード
   * @param resultCode base64エンコードされた受験結果コード
   * @param allAnswer すべての解答データ配列
   */
  putExamEnd(entryCode: string, resultCode: string, allAnswer: PutAllQuestionAnswer[]) {
    const config = {
      headers: {
        'x-exam-key': process.env.VUE_APP_EXAM_SECRET_KEY,
      },
    };
    return Repository.put(`${this.repository}/${entryCode}/results/${resultCode}/complete`,
      { questions: this.convert.serializeArray(allAnswer, PutAllQuestionAnswer) }, config);
  }

  /**
   * 受験強制終了
   */
  putForceExamEnd() {
    const config = {
      headers: {
        'x-exam-key': process.env.VUE_APP_EXAM_SECRET_KEY,
      },
    };
    return Repository.put(`${this.repository}/results`, null, config);
  }

  /**
   * 購入履歴取得
   */
  getSettlementCartHistory() {
    const config = {
      headers: {
        'x-exam-key': process.env.VUE_APP_EXAM_SECRET_KEY,
      },
    };
    // eslint-disable-next-line @typescript-eslint/camelcase
    return Repository.get(`${this.repository}_history`, config);
  }
}
